import { useCallback, useRef, useEffect } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { useLiveKit } from './useLiveKit'
import { useRealtimeSTT } from './useRealtimeSTT'
import { useMediaRecorder } from './useMediaRecorder'
import { useTextStream } from './useTextStream'
import type { WordTiming } from './useTextStream'
import {
  createSession,
  getSessionInfo,
  startSession,
  closeSession,
  finalizeSession,
  sendTTSToSession,
  saveSegmentMetadata,
  segmentStart,
  segmentEnd,
} from '@/api/interview'
import type { InterviewConfig, InterviewSegment } from '@/types/interview'
import { classifyGetUserMediaError } from '@/utils/media'
import { useDeploymentConfig, applyPlaceholders } from '@/context/DeploymentConfigContext'
import type { DeploymentConfig } from '@/api/deployment'
import { buildSegments } from '@/utils/segments'

// Candidate flow historically uses 'outro' as the closing-segment key (see
// getScript / response-window logic). The /v1/public/* join flow uses
// 'closing' to match the backend. Keep this local constant so the candidate
// flow keeps its existing key without affecting the shared util's default.
const CANDIDATE_FLOW_STRUCTURE = {
  introEnabled: true,
  closingEnabled: true,
  closingKey: 'outro',
} as const
const RESPONSE_TIMEOUT_MS = 120_000
const INTRO_TIMEOUT_MS = 60_000
// Open the Start gate this long after connect if BEY's avatar never attaches
// (audio-only mode / dropped video), so the button isn't stuck looking dead.
const AVATAR_FALLBACK_MS = 8_000
const MS_PER_WORD_FALLBACK = 550 // matches candidate.js
const OUTRO_LINGER_MS = 1500 // avatar bleibt nach letztem Satz stehen, bevor Thank-You-Splash erscheint

function getScript(
  segment: string,
  config: InterviewConfig,
  deploymentConfig: DeploymentConfig,
): string {
  const texts = deploymentConfig.texts[config.language]
  if (segment === 'intro') {
    return applyPlaceholders(texts.intro, {
      companyName: deploymentConfig.company_name,
      jobTitle: deploymentConfig.job_title,
    })
  }
  if (segment === 'outro') return texts.outro
  const qMatch = segment.match(/^q(\d+)$/)
  if (qMatch) {
    const idx = parseInt(qMatch[1], 10) - 1
    if (idx === 0 && config.question1) return config.question1
    if (idx === 1 && config.question2) return config.question2
    return texts.fallback_questions[idx] ?? ''
  }
  return ''
}

function getQuestionContext(segment: string, config: InterviewConfig): string {
  if (segment === 'intro') return 'Introduction segment'
  if (segment === 'outro') return 'Final segment'
  const qMatch = segment.match(/^q(\d+)$/)
  if (qMatch) {
    const idx = parseInt(qMatch[1], 10) - 1
    const text = (idx === 0 ? config.question1 : idx === 1 ? config.question2 : undefined) ?? ''
    return `Question ${qMatch[1]}: ${text}`
  }
  return ''
}

function getNextContext(nextSegment: string | undefined, config: InterviewConfig): string {
  if (!nextSegment) return 'The interview will conclude after this.'
  if (nextSegment === 'outro') return 'outro'
  const qMatch = nextSegment.match(/^q(\d+)$/)
  if (qMatch) {
    const idx = parseInt(qMatch[1], 10) - 1
    const text = (idx === 0 ? config.question1 : idx === 1 ? config.question2 : undefined) ?? ''
    return `Next question will be: ${text}`
  }
  return ''
}

export function useInterview() {
  const store = useInterviewStore()
  const deploymentConfig = useDeploymentConfig()
  const sessionIdRef = useRef<string | null>(null)
  const sessionTokenRef = useRef<string | null>(null)
  const segmentIndexRef = useRef(0)
  const segmentsRef = useRef<string[]>(buildSegments(2, CANDIDATE_FLOW_STRUCTURE))
  const responseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const speechFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const audioEndTimeRef = useRef<number>(0) // performance.now() when outro audio should finish
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const advancingRef = useRef(false)
  const startingRef = useRef(false)
  const activeRecordingSegmentRef = useRef<string | null>(null)

  // Word timing state: populated by word_timings LiveKit data event, consumed by speech_started.
  // Two-flag latch: both must arrive before animation starts (LiveKit ordering is not guaranteed).
  const pendingWordTimingsRef = useRef<{ text: string; words: WordTiming[] } | null>(null)
  const speechStartedPendingRef = useRef(false) // true when speech_started arrived before word_timings

  // Guards against stale speech_completed from reaction killing the next segment's avatar_speaking.
  // Only set true when speech_started fires during avatar_speaking phase.
  const speechStartedForSegmentRef = useRef(false)

  // Tracks when the reaction's speech_completed fires during avatar_reacting.
  // After this, any further word_timings are from the worker's prefetch of the NEXT segment.
  const reactionSpeechDoneRef = useRef(false)

  // Buffer for word_timings the worker sends for the NEXT segment (prefetch) during avatar_reacting.
  // Used by loadSegment to start animation immediately and skip duplicate TTS.
  const nextSegmentBufferRef = useRef<{ text: string; words: WordTiming[] } | null>(null)

  // Authoritative script text for the current segment — always the full string we passed to TTS.
  // Used in onSpeechStarted to override the worker's `text` field which can be truncated.
  const currentScriptRef = useRef<string>('')

  // Tracks the last reaction text spoken by the avatar, for transcript archiving
  const reactionTextRef = useRef<string>('')

  // Imperative text stream for avatar speech (timing-based reveal)
  const textStream = useTextStream()

  // Stable refs to break circular dependencies and avoid stale closures
  const endInterviewRef = useRef<() => Promise<void>>(() => Promise.resolve())
  const loadSegmentRef = useRef<(s: InterviewSegment) => Promise<void>>(() => Promise.resolve())
  const advanceSegmentRef = useRef<() => Promise<void>>(() => Promise.resolve())

  // ── onSpeechCompleted — stable ref pattern, reads live state via getState() ──

  // The implementation: no stale closures, always reads current store state
  function onSpeechCompletedImpl(segment: string) {
    const { config, currentPhase } = useInterviewStore.getState()
    if (!config) return

    // For reactions: snap text to full, then mark reaction done so we can detect
    // the worker's prefetched next-segment events arriving after this point.
    if (currentPhase === 'avatar_reacting') {
      textStream.complete()
      reactionSpeechDoneRef.current = true
      return
    }

    const currentSegment = segmentsRef.current[segmentIndexRef.current]
    if (segment && segment !== currentSegment) return
    if (currentPhase !== 'avatar_speaking') return

    // Reject stale speech_completed events (e.g. reaction's speech_completed arriving
    // during the next segment's avatar_speaking phase). Only accept if we already
    // received a speech_started for THIS segment.
    if (!speechStartedForSegmentRef.current) return

    // Clear fallback timer — real speech_completed event arrived first
    if (speechFallbackTimerRef.current) {
      clearTimeout(speechFallbackTimerRef.current)
      speechFallbackTimerRef.current = null
    }

    // Complete any in-progress text reveal
    textStream.complete()

    if (currentSegment === 'outro') {
      void endInterviewRef.current()
      return
    }

    // Mark response start NOW — avatar just finished, user is about to speak
    recorder.markSegmentStart()

    const s = useInterviewStore.getState()
    s.setRealtimeTranscript('')
    s.setPhase('user_response')
    s.setAgentMessage(config.language === 'de'
      ? 'Bitte gib deine Antwort und klicke dann auf "Weiter"'
      : 'Please give your answer then click "Next"')

    // Start streaming audio to STT only now (after avatar finishes speaking)
    stt.startAudio()

    const timeoutMs = currentSegment === 'intro' ? INTRO_TIMEOUT_MS : RESPONSE_TIMEOUT_MS
    responseTimerRef.current = setTimeout(() => {
      void advanceSegmentRef.current()
    }, timeoutMs)
  }

  const onSpeechCompletedRef = useRef(onSpeechCompletedImpl)

  // Stable wrapper — identity never changes, always calls latest impl via ref
  const onSpeechCompleted = useCallback((segment: string) => {
    onSpeechCompletedRef.current(segment)
  }, [])

  const stt = useRealtimeSTT({
    getSessionId: () => sessionIdRef.current ?? '',
    getSessionToken: () => sessionTokenRef.current ?? '',
    onReactionReady: () => {},
    onPartialTranscript: (text) => {
      useInterviewStore.getState().setRealtimeTranscript(text)
    },
    onFinalTranscript: (segment, text) => {
      if (text) {
        const s = useInterviewStore.getState()
        s.updateTranscript(segment as InterviewSegment, text)
        s.setCleanedTranscript(text)
      }
    },
    onError: (msg) => console.error('STT error:', msg),
  })

  const recorder = useMediaRecorder()

  // Update the impl ref after every render so it captures the latest stt/recorder
  useEffect(() => { onSpeechCompletedRef.current = onSpeechCompletedImpl })

  const { connect: liveKitConnect, disconnect: liveKitDisconnect, reattachTracks, publishStream: liveKitPublishStream, replaceLocalTrack, videoElRef, audioElRef, waitForAudioEnd } = useLiveKit({
    onAvatarReady: () => {
      const s = useInterviewStore.getState()
      s.setStatus('ready')
      const lang = s.config?.language
      s.setAgentMessage(lang === 'de' ? 'Bereit zum Starten des Interviews!' : 'Ready to start the interview!')
    },
    onAvatarVideoAttached: () => {
      useInterviewStore.getState().setAvatarReady(true)
    },
    onAvatarUnavailable: (_reason: string) => {
      // Worker hit BEY's concurrency limit (or BEY otherwise refused).
      // Block Start, show the candidate the unavailable overlay.
      useInterviewStore.getState().setAvatarUnavailable(true)
    },
    onAvatarDegraded: () => {
      // BEY persistent 429 — worker is alive in audio-only fallback mode.
      // Show the "continue without avatar" overlay; candidate dismisses
      // it to proceed.
      useInterviewStore.getState().setAvatarDegraded(true)
    },
    onQueueStatus: (position: number, etaSeconds: number, queueDepth: number) => {
      useInterviewStore.getState().setQueueStatus(position, etaSeconds, queueDepth)
    },
    onSpeechCompleted,
    onDisconnected: () => {
      if (useInterviewStore.getState().status === 'running') void endInterviewRef.current()
    },
    onWordTimings: (text, words) => {
      const phase = useInterviewStore.getState().currentPhase

      // During avatar_reacting: distinguish reaction events from prefetched next-segment events.
      if (phase === 'avatar_reacting') {
        if (!reactionSpeechDoneRef.current) {
          // Still the reaction — track for archiving
          reactionTextRef.current = text
          pendingWordTimingsRef.current = { text, words }
        } else {
          // Reaction is done — this is the worker's prefetch of the next segment.
          // Buffer it so loadSegment can use it (avoids double-speak).
          nextSegmentBufferRef.current = { text, words }
        }
        return
      }

      pendingWordTimingsRef.current = { text, words }

      // If speech_started already fired (fallback is showing), upgrade to word-by-word animation
      if (speechStartedPendingRef.current) {
        speechStartedPendingRef.current = false
        pendingWordTimingsRef.current = null
        const fullText = phase === 'avatar_speaking' && currentScriptRef.current
          ? currentScriptRef.current
          : text
        if (words.length > 0) {
          textStream.startWithTimings(fullText, words, performance.now())
        }
      }
    },
    onSpeechStarted: () => {
      const phase = useInterviewStore.getState().currentPhase

      // During avatar_reacting: ignore speech_started for prefetched next segment
      // (the buffered word_timings are enough; loadSegment will use them)
      if (phase === 'avatar_reacting') {
        if (!reactionSpeechDoneRef.current) {
          // Reaction speech_started — trigger animation for reaction text
          const pending = pendingWordTimingsRef.current
          if (pending && pending.words.length > 0) {
            pendingWordTimingsRef.current = null
            textStream.startWithTimings(pending.text, pending.words, performance.now())
          }
        }
        return
      }

      // Mark that we received speech_started for THIS segment (used as guard in onSpeechCompleted)
      if (phase === 'avatar_speaking') {
        speechStartedForSegmentRef.current = true
      }

      const pending = pendingWordTimingsRef.current
      const fullText = phase === 'avatar_speaking' && currentScriptRef.current
        ? currentScriptRef.current
        : (pending?.text ?? '')

      if (pending && pending.words.length > 0) {
        // Best case: word_timings arrived first — start word-by-word animation
        pendingWordTimingsRef.current = null
        speechStartedPendingRef.current = false
        textStream.startWithTimings(fullText, pending.words, performance.now())
      } else if (fullText) {
        // word_timings not yet arrived — show full text immediately as fallback
        // (if word_timings arrives later, onWordTimings will upgrade to animation)
        speechStartedPendingRef.current = true
        textStream.startFallback(fullText)
      }
    },
  })

  // ── Setup ─────────────────────────────────────────────────────────────────

  const setup = useCallback(async (config: InterviewConfig) => {
    store.reset()
    store.setConfig(config)
    store.setStatus('setup')
    store.setAgentMessage(config.language === 'de' ? 'Verbindung wird hergestellt...' : 'Establishing connection...')

    // Reset refs to prevent stale state from previous interviews
    sessionIdRef.current = null
    sessionTokenRef.current = null
    segmentIndexRef.current = 0
    advancingRef.current = false

    // Provisional segments based on mode — corrected from server after getSessionInfo
    segmentsRef.current = buildSegments(config.mode === 'demo' ? 1 : 2, CANDIDATE_FLOW_STRUCTURE)

    try {
      const { session_id, session_token } = await createSession(config)
      sessionIdRef.current = session_id
      sessionTokenRef.current = session_token ?? null

      await startSession(session_id)

      const info = await getSessionInfo(session_id)
      const questionCount = info.total_questions ?? (config.mode === 'demo' ? 1 : 2)
      const segments = buildSegments(questionCount, CANDIDATE_FLOW_STRUCTURE)
      segmentsRef.current = segments
      store.setSegments(segments)
      store.setSession(session_id, info.livekit_url ?? '', info.livekit_token ?? '', info.avatar_enabled ?? false)

      if (info.livekit_url && info.livekit_token) {
        await liveKitConnect(info.livekit_url, info.livekit_token)
        if (!info.avatar_enabled) {
          store.setStatus('ready')
          store.setAgentMessage(config.language === 'de' ? 'Bereit zum Starten des Interviews!' : 'Ready to start the interview!')
        } else {
          // Avatar mode: onAvatarReady sets status to 'ready' when bey-avatar-agent joins.
          // Fallback after AVATAR_FALLBACK_MS in case BEY never connects.
          setTimeout(() => {
            if (useInterviewStore.getState().status === 'setup') {
              console.warn('[setup] Avatar timeout — falling back to audio-only mode')
              useInterviewStore.getState().setStatus('ready')
              useInterviewStore.getState().setAgentMessage(
                config.language === 'de' ? 'Bereit zum Starten des Interviews!' : 'Ready to start the interview!'
              )
            }
          }, AVATAR_FALLBACK_MS)
          // Parallel fallback for the video-attached signal — unblocks the
          // Start button + hides the loading overlay even if BEY's video
          // never lands. Same budget as the status fallback above.
          setTimeout(() => {
            if (!useInterviewStore.getState().avatarReady) {
              console.warn('[setup] Avatar video timeout — proceeding without video')
              useInterviewStore.getState().setAvatarReady(true)
            }
          }, AVATAR_FALLBACK_MS)
        }
      } else {
        store.setStatus('ready')
      }
    } catch (err) {
      console.error('Setup failed:', err)
      store.setStatus('idle')
      throw err
    }
  }, [liveKitConnect, store])

  // ── Start ─────────────────────────────────────────────────────────────────

  const startInterview = useCallback(async () => {
    const config = store.config
    const sessionId = sessionIdRef.current
    if (!config || !sessionId) throw new Error('Not configured')

    // Re-entrancy guard: the Start button stays enabled until status flips
    // to 'running' further down, and the getUserMedia prompt can hold that
    // window open for seconds. A second click would spawn a second stream
    // (leaked, camera stays on), a second STT socket and a second interval.
    if (startingRef.current) return
    startingRef.current = true
    try {
      segmentIndexRef.current = 0
      advancingRef.current = false
      store.setMediaError(null)

      // Get stream FIRST — so streamRef.current is populated before status → 'running'
      // (SelfVideo reads streamRef.current when isRecording becomes true)
      let stream: MediaStream
      try {
        stream = await recorder.startContinuous()
      } catch (err) {
        console.error('Failed to start recording:', err)
        store.setMediaError(classifyGetUserMediaError(err))
        throw err
      }

      // Publish camera + audio to LiveKit so egress can record the participant.
      // Must complete before loadSegment triggers segment_start (which starts egress).
      await liveKitPublishStream(stream)

      store.setStatus('running')
      store.setPhase('waiting')

      // Open STT WebSocket (but don't start streaming audio yet — that happens after avatar speaks)
      try {
        await stt.connect(stream)
      } catch (err) {
        console.warn('STT connection failed, continuing without STT:', err)
      }

      // Recording elapsed timer
      let elapsed = 0
      elapsedTimerRef.current = setInterval(() => {
        elapsed++
        store.setRecordingElapsed(elapsed)
      }, 1000)

      await loadSegmentRef.current(segmentsRef.current[0])
    } finally {
      startingRef.current = false
    }
  }, [recorder, stt, store])

  // ── Segment ───────────────────────────────────────────────────────────────

  const loadSegment = useCallback(async (segment: InterviewSegment) => {
    const config = store.config
    const sessionId = sessionIdRef.current
    if (!config || !sessionId) return

    textStream.reset()

    store.setSegment(segment)
    store.setPhase('avatar_speaking')
    store.setRealtimeTranscript('')

    const script = getScript(segment, config, deploymentConfig)
    store.setAgentMessage(script)

    // Store full script as authoritative text — worker's word_timings `text` field can be truncated
    currentScriptRef.current = script

    // Reset all speech event state for the new segment
    pendingWordTimingsRef.current = null
    speechStartedPendingRef.current = false
    speechStartedForSegmentRef.current = false
    reactionSpeechDoneRef.current = false
    textStream.preload(script)

    // Check if the worker already prefetched and spoke this segment during avatar_reacting.
    // If so, use the buffered word timings directly — no need to send duplicate TTS.
    const buffered = nextSegmentBufferRef.current
    nextSegmentBufferRef.current = null

    if (buffered && buffered.words.length > 0) {
      // Worker prefetched this segment — start animation immediately.
      // Don't set speechStartedForSegmentRef: since we skipped TTS, no speech_completed
      // will arrive for this segment. The fallback timer handles phase transition.
      // (This also blocks stale speech_completed events from the reaction.)
      textStream.startWithTimings(script, buffered.words, performance.now())
    } else {
      // Normal path — send TTS to backend, wait for worker events
      try {
        await sendTTSToSession(sessionId, script, segment)
      } catch (err) {
        console.warn('sendTTS failed:', err)
      }
    }

    // Start server-side egress recording for this segment (fire-and-forget)
    activeRecordingSegmentRef.current = segment
    segmentStart(sessionId, segment).catch((e) => console.warn('segmentStart failed:', e))

    // Fallback timer: triggers speech completion if speech_completed event never arrives.
    // For buffered (prefetched) segments, use the actual word timing duration + margin.
    // For normal segments, estimate from word count.
    if (speechFallbackTimerRef.current) clearTimeout(speechFallbackTimerRef.current)
    let fallbackMs: number
    if (buffered && buffered.words.length > 0) {
      const lastWord = buffered.words[buffered.words.length - 1]
      fallbackMs = lastWord.offset_ms + lastWord.duration_ms + 500 // 500ms margin
    } else {
      const wordCount = script.trim().split(/\s+/).length
      fallbackMs = wordCount * MS_PER_WORD_FALLBACK
    }
    audioEndTimeRef.current = performance.now() + fallbackMs
    speechFallbackTimerRef.current = setTimeout(() => {
      speechFallbackTimerRef.current = null
      // For buffered segments, directly trigger completion since speechStartedForSegmentRef is false
      speechStartedForSegmentRef.current = true
      onSpeechCompleted(segment)
    }, fallbackMs)
  }, [store, onSpeechCompleted, deploymentConfig])

  useEffect(() => { loadSegmentRef.current = loadSegment }, [loadSegment])

  // ── Advance ───────────────────────────────────────────────────────────────

  const advanceSegment = useCallback(async () => {
    if (advancingRef.current) return
    const config = store.config
    const sessionId = sessionIdRef.current
    if (!config || !sessionId) return

    advancingRef.current = true
    if (responseTimerRef.current) {
      clearTimeout(responseTimerRef.current)
      responseTimerRef.current = null
    }

    textStream.reset()
    store.setPhase('avatar_reacting')
    store.setAgentMessage(config.language === 'de' ? 'Wird verarbeitet...' : 'Processing...')
    reactionTextRef.current = ''
    reactionSpeechDoneRef.current = false
    nextSegmentBufferRef.current = null

    const currentSegment = segmentsRef.current[segmentIndexRef.current]

    // Flush MediaRecorder buffer + snapshot blob with timing (matches candidate.js requestData flow)
    const { startTime, endTime } = await recorder.markSegmentEnd(currentSegment)

    // Stop server-side egress recording for this segment (fire-and-forget)
    const segToEnd = activeRecordingSegmentRef.current
    activeRecordingSegmentRef.current = null
    if (segToEnd) segmentEnd(sessionId, segToEnd).catch((e) => console.warn('segmentEnd failed:', e))

    store.addTranscript({
      segment: currentSegment,
      text: config.language === 'de' ? 'Transkript wird verarbeitet...' : 'Processing transcript...',
      startTime,
      endTime,
      speaker: 'candidate',
      content: '',
      timestamp: new Date(),
    })

    // Save segment metadata + fire transcription + video upload in background
    saveSegmentMetadata({
      session_id: sessionId,
      segment: currentSegment,
      start_time: startTime,
      end_time: endTime,
      language: config.language,
    }).catch((e) => console.warn('Segment metadata save failed:', e))

    // Use real-time STT transcript (already saved to DB by websocket handler) instead of
    // re-transcribing via batch Celery job — saves Azure STT cost and eliminates latency.
    // Video recording is handled by LiveKit egress (server-side) — no client upload needed.
    const realtimeText = useInterviewStore.getState().realtimeTranscript
    if (realtimeText) {
      store.updateTranscript(currentSegment, realtimeText)
    }

    // Get reaction from STT — with 30s timeout built into sendStopAndReact
    const nextIndex = segmentIndexRef.current + 1
    const nextSegment = segmentsRef.current[nextIndex]
    // Pre-compute next segment script so worker can pre-synthesize it during reaction playback
    const nextScript = nextSegment ? getScript(nextSegment, config, deploymentConfig) : ''
    let reactionDurationMs = 6000
    try {
      const qCtx = getQuestionContext(currentSegment, config)
      const nCtx = getNextContext(nextSegment, config)
      const reaction = await stt.sendStopAndReact(currentSegment, qCtx, nCtx, nextScript)
      reactionDurationMs = reaction?.durationMs ?? 6000
    } catch (e) {
      console.warn('STT react failed:', e)
    }

    // Short natural pause — worker pre-synthesizes next question during reaction playback
    const bufferMs = 300
    await new Promise((r) => setTimeout(r, reactionDurationMs + bufferMs))

    // endInterview may have run while we waited (manual abort, disconnect) —
    // don't load another segment or re-end an already-closed session.
    if (useInterviewStore.getState().status !== 'running') {
      advancingRef.current = false
      return
    }

    segmentIndexRef.current = nextIndex
    advancingRef.current = false

    if (nextSegment) {
      await loadSegmentRef.current(nextSegment)
    } else {
      await endInterviewRef.current()
    }
  }, [recorder, stt, store, deploymentConfig])

  // ── End ───────────────────────────────────────────────────────────────────

  const endInterview = useCallback(async (aborted = false) => {
    if (speechFallbackTimerRef.current) {
      clearTimeout(speechFallbackTimerRef.current)
      speechFallbackTimerRef.current = null
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }
    if (responseTimerRef.current) {
      clearTimeout(responseTimerRef.current)
      responseTimerRef.current = null
    }
    const sessionId = sessionIdRef.current

    // Stop egress for the current segment if still active (e.g. outro ends here)
    const segToEnd = activeRecordingSegmentRef.current
    activeRecordingSegmentRef.current = null
    if (sessionId && segToEnd) segmentEnd(sessionId, segToEnd).catch((e) => console.warn('segmentEnd failed:', e))

    // On a natural finish, let the closing line play out and show the
    // finalizing overlay. On a manual abort the candidate wants out now —
    // skip the wait entirely (mirrors useJoinInterview).
    if (!aborted) {
      // Wait only for the actual remaining audio duration, not a 15s blanket timeout.
      // audioEndTimeRef is set in loadSegment to performance.now() + fallbackMs, so
      // remaining ≈ 0 when the fallback fires (audio just done) and ≈ real remaining
      // time when speech_completed fires early (live-stream path).
      const remainingMs = Math.max(0, audioEndTimeRef.current - performance.now())
      await waitForAudioEnd(remainingMs)
      store.setPhase('finalizing')
      await new Promise<void>((resolve) => setTimeout(resolve, OUTRO_LINGER_MS))
    }

    store.setAborted(aborted)
    store.setStatus('complete')
    store.setPhase('waiting')

    try {
      recorder.stopStream()
      stt.disconnect()
      await liveKitDisconnect()
      if (sessionId) {
        await finalizeSession(sessionId)
        await closeSession(sessionId)
      }
    } catch (err) {
      console.warn('Cleanup error:', err)
    }
  }, [liveKitDisconnect, recorder, stt, store, waitForAudioEnd])

  useEffect(() => { endInterviewRef.current = endInterview }, [endInterview])
  useEffect(() => { advanceSegmentRef.current = advanceSegment }, [advanceSegment])

  // ── Unmount safety net ─────────────────────────────────────────────────────
  // endInterview only runs on the happy path. If the user navigates away
  // mid-interview (browser back, route change) the layout unmounts with the
  // camera LED on, timers ticking, the LiveKit room connected and the hidden
  // avatar <audio> in document.body still playing. Everything called here is
  // idempotent, so running it after a normal endInterview is harmless.
  const teardownRef = useRef<() => void>(() => {})
  useEffect(() => {
    teardownRef.current = () => {
      if (responseTimerRef.current) clearTimeout(responseTimerRef.current)
      if (speechFallbackTimerRef.current) clearTimeout(speechFallbackTimerRef.current)
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
      recorder.stopStream()
      stt.disconnect()
      void liveKitDisconnect()
    }
  })
  useEffect(() => () => { teardownRef.current() }, [])

  return {
    setup,
    startInterview,
    advanceSegment,
    endInterview,
    reattachTracks,
    replaceLocalTrack,
    requestPreview: recorder.requestPreview,
    sessionIdRef,
    streamRef: recorder.streamRef,
    videoElRef,
    audioElRef,
    textStream,
    reactionTextRef,
  }
}
